import { BookOpen } from 'lucide-react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { guidePage } from './registry.ts';
import type { GuideKey } from './types.ts';
import './guide-button.css';

const GuideDialog = lazy(() => import('./GuideDialog.tsx'));
const preloadDialog = () => void import('./GuideDialog.tsx');

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
}

/**
 * Topbar “คู่มือ” button (icon + label on desktop, icon only on phones) and the `?` shortcut.
 * The dialog code and the guide content load on first use.
 */
export function GuideButton({ guideKey }: { guideKey: GuideKey }) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const page = guidePage(guideKey);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '?' || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      if (isTypingTarget(e.target) || document.querySelector('[aria-modal="true"]')) return;
      e.preventDefault();
      setMounted(true);
      setOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [guideKey]);

  return (
    <>
      <button
        type="button"
        className="guide-btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-keyshortcuts="Shift+Slash"
        aria-label={`คู่มือการใช้งาน ${page.label}`}
        title="คู่มือการใช้งาน (กด ?)"
        data-guide-key={guideKey}
        onPointerEnter={preloadDialog}
        onFocus={preloadDialog}
        onClick={() => {
          setMounted(true);
          setOpen(true);
        }}
      >
        <BookOpen size={17} strokeWidth={1.9} aria-hidden="true" />
        <span className="guide-btn__label" aria-hidden="true">
          คู่มือ
        </span>
      </button>
      {mounted && (
        <Suspense fallback={null}>
          <GuideDialog open={open} onClose={() => setOpen(false)} guideKey={guideKey} />
        </Suspense>
      )}
    </>
  );
}
